/********************************************************************
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this
 * file in accordance with the terms of the Adobe license agreement
 * accompanying it.
 *******************************************************************/

import { FunctionComponent, VNode } from 'preact';
import { HTMLAttributes } from 'preact/compat';
import { classes } from '@adobe-commerce/elsie/lib';
import '@adobe-commerce/elsie/components/RadioButton/RadioButton.css';

export interface RadioButtonProps
  extends Omit<HTMLAttributes<HTMLInputElement>, 'size' | 'label' | 'icon'> {
  label: string | VNode<HTMLAttributes<HTMLElement>>;
  name: string;
  value: string;
  size?: 'medium' | 'large';
  checked?: boolean;
  disabled?: boolean;
  error?: boolean;
  description?: string;
  busy?: boolean;
  icon?:
    | VNode<HTMLAttributes<SVGSVGElement>>
    | VNode<HTMLAttributes<HTMLImageElement>>;
}

export const RadioButton: FunctionComponent<RadioButtonProps> = ({
  name,
  label,
  value,
  size = 'medium',
  checked = false,
  disabled = false,
  error = false,
  description = '',
  busy = false,
  icon,
  className,
  children,
  ...props
}) => {
  return (
    <label
      className={classes([
        className,
        'dropin-radio-button',
        ['dropin-radio-button--error', error],
        ['dropin-radio-button--disabled', disabled],
      ])}
    >
      <input
        name={name}
        value={value}
        checked={checked}
        disabled={disabled}
        type="radio"
        className={classes([
          'dropin-radio-button__input',
          ['dropin-radio-button__input--error', error],
          ['dropin-radio-button__input--disabled', disabled],
        ])}
        aria-busy={busy}
        {...props}
      />
      <span
        className={classes([
          'dropin-radio-button__label',
          `dropin-radio-button__label--${size}`,
          ['dropin-radio-button__label--error', error],
          ['dropin-radio-button__label--disabled', disabled],
        ])}
      >
        {icon && (
          // @ts-ignore
          <icon.type
            {...icon?.props}
            className={classes([
              'dropin-radio-button__icon',
              icon?.props?.className,
            ])}
          />
        )}
        {label}
      </span>
      <span
        className={classes([
          'dropin-radio-button__description',
          `dropin-radio-button__description--${size}`,
          ['dropin-radio-button__description--disabled', disabled],
        ])}
      >
        {description}
      </span>
    </label>
  );
};
